import { createHash } from "node:crypto";
import { eventToTelemetrySpan } from "@fabric-harness/sdk";
import type { FabricEvent, SubmissionTelemetrySink, TelemetrySpan } from "@fabric-harness/sdk";
import type { DatabricksRawProtocolClient } from "./raw-protocol-client.js";

/**
 * MLflow trace export (v2 migration, workstream C6): one MLflow trace per
 * settled submission, so agent activity shows up in the workspace's MLflow
 * experiment UI next to models and evaluations.
 *
 * Wiring: `sink` goes into `createSubmissionRunner({ telemetry })` and
 * `onEvent` fans in with the app's `onEvent` callback. A `submission_started`
 * telemetry event opens a span buffer keyed by the store session id
 * (`agent:<agent>:<instanceId>:<session>` — the `sessionId` stamped on
 * `FabricEvent`s during the attempt); every event that matches an open buffer
 * and converts via the SDK's `eventToTelemetrySpan` (i.e. carries
 * `data.durationMs`) is appended as a child span. `submission_settled` builds
 * the trace and POSTs it to the MLflow **V3 traces API**
 * (`POST /api/3.0/mlflow/traces`), then drops the buffer.
 *
 * The V3 payload ({@link buildMlflowTrace}) is implemented to the published
 * API spec — OTLP-shaped spans with base64 ids, unix-nano timestamps and
 * typed attributes — but live verification requires a workspace. Set
 * `DATABRICKS_LIVE_TEST=1` and `DATABRICKS_MLFLOW_EXPERIMENT_ID`, then run
 * `pnpm --filter @fabric-harness/databricks test:live` before relying on it.
 *
 * Both `sink.record` and `onEvent` never throw — failures (including the
 * async POST) are reported through `onError`.
 */
export interface MlflowTraceExporterOptions {
  client: DatabricksRawProtocolClient;
  /** MLflow experiment receiving the traces. */
  experimentId: string;
  /** Failure reporter (default console.error). */
  onError?: (context: string, error: unknown) => void;
}

export interface MlflowTraceExporter {
  /** Wire into `createSubmissionRunner({ telemetry })`. */
  sink: SubmissionTelemetrySink;
  /** Fan in with the app's `onEvent` to capture per-turn/tool spans. */
  onEvent: (event: FabricEvent) => void;
}

interface TraceBuffer {
  submissionId: string;
  agent: string;
  instanceId: string;
  session: string;
  startedAtMs: number;
  attemptCount?: number;
  tenantId?: string;
  spans: TelemetrySpan[];
}

export function mlflowTraceExporter(options: MlflowTraceExporterOptions): MlflowTraceExporter {
  const onError =
    options.onError ??
    ((context: string, error: unknown) => {
      console.error(`[fabric-harness:mlflow-tracing] ${context}:`, error);
    });
  /** Open buffers keyed by store session id (`agent:<agent>:<instanceId>:<session>`). */
  const buffers = new Map<string, TraceBuffer>();

  return {
    sink: {
      record(event) {
        try {
          const key = `agent:${event.agent}:${event.instanceId}:${event.session}`;
          if (event.type === "submission_running" || event.type === "submission_started") {
            buffers.set(key, {
              submissionId: event.submissionId,
              agent: event.agent,
              instanceId: event.instanceId,
              session: event.session,
              startedAtMs: event.at,
              attemptCount: event.attemptCount,
              ...(event.tenantId !== undefined ? { tenantId: event.tenantId } : {}),
              spans: [],
            });
            return;
          }
          if (event.type !== "submission_settled") return;
          const buffer = buffers.get(key);
          buffers.delete(key);
          // Settlement without an observed start (exporter attached late) still
          // produces a trace: derive the start from the settlement duration.
          const startedAtMs = buffer?.startedAtMs ?? event.at - (event.durationMs ?? 0);
          const attemptCount = event.attemptCount ?? buffer?.attemptCount;
          const trace = buildMlflowTrace({
            submissionId: event.submissionId,
            agent: event.agent,
            instanceId: event.instanceId,
            session: event.session,
            experimentId: options.experimentId,
            startedAtMs,
            settledAtMs: event.at,
            state: event.outcome === "completed" ? "OK" : "ERROR",
            spans: buffer?.spans ?? [],
            ...(attemptCount !== undefined ? { attemptCount } : {}),
            ...(event.tenantId !== undefined ? { tenantId: event.tenantId } : {}),
            ...(event.error !== undefined ? { error: event.error } : {}),
          });
          void options.client
            .post("/api/3.0/mlflow/traces", trace)
            .catch((error) => onError(`trace export failed for ${event.submissionId}`, error));
        } catch (error) {
          onError("telemetry event handling failed", error);
        }
      },
    },
    onEvent(event) {
      try {
        if (!event.sessionId) return;
        const buffer = buffers.get(event.sessionId);
        if (!buffer) return;
        const span = eventToTelemetrySpan(event);
        if (span) buffer.spans.push(span);
      } catch (error) {
        onError("event buffering failed", error);
      }
    },
  };
}

// ─── Pure payload builder ────────────────────────────────────────────────────

export interface MlflowTraceInput {
  submissionId: string;
  agent: string;
  instanceId: string;
  session: string;
  experimentId: string;
  /** Epoch ms when the submission started executing. */
  startedAtMs: number;
  /** Epoch ms when the submission settled. */
  settledAtMs: number;
  state: "OK" | "ERROR";
  /** Child spans (from `eventToTelemetrySpan`) buffered during execution. */
  spans: TelemetrySpan[];
  attemptCount?: number;
  tenantId?: string;
  error?: string;
}

interface OtlpAttribute {
  key: string;
  value: { string_value: string } | { double_value: number } | { int_value: string };
}

interface MlflowSpan {
  trace_id: string;
  span_id: string;
  parent_span_id?: string;
  name: string;
  start_time_unix_nano: string;
  end_time_unix_nano: string;
  attributes: OtlpAttribute[];
  status: { code: "STATUS_CODE_OK" | "STATUS_CODE_ERROR"; message?: string };
}

export interface MlflowTracePayload {
  trace: {
    trace_info: {
      trace_id: string;
      trace_location: {
        type: "MLFLOW_EXPERIMENT";
        mlflow_experiment: { experiment_id: string };
      };
      request_time: string;
      execution_duration: string;
      state: "OK" | "ERROR";
      trace_metadata: Record<string, string>;
      tags: Record<string, string>;
    };
    trace_data: { spans: MlflowSpan[] };
  };
}

/**
 * Build the MLflow V3 `POST /api/3.0/mlflow/traces` payload for one settled
 * submission. Pure and deterministic: trace/span ids derive from the
 * submission id (+ span index) via SHA-256, so replaying the same settled
 * submission produces byte-identical ids. The root span covers the whole
 * submission; buffered {@link TelemetrySpan}s become its children.
 */
export function buildMlflowTrace(input: MlflowTraceInput): MlflowTracePayload {
  const traceBytes = sha256(input.submissionId).subarray(0, 16);
  const traceIdBase64 = traceBytes.toString("base64");
  const rootSpanId = spanIdFor(input.submissionId, 0);
  const durationSeconds = Math.max(0, input.settledAtMs - input.startedAtMs) / 1000;

  const rootSpan: MlflowSpan = {
    trace_id: traceIdBase64,
    span_id: rootSpanId,
    name: `fabric.submission ${input.agent}`,
    start_time_unix_nano: toUnixNano(input.startedAtMs),
    end_time_unix_nano: toUnixNano(input.settledAtMs),
    attributes: toOtlpAttributes({
      "fabric.submission_id": input.submissionId,
      "fabric.agent": input.agent,
      "fabric.instance_id": input.instanceId,
      "fabric.session": input.session,
      ...(input.attemptCount !== undefined ? { "fabric.attempt_count": input.attemptCount } : {}),
      ...(input.error !== undefined ? { "fabric.error": input.error } : {}),
    }),
    status: {
      code: input.state === "OK" ? "STATUS_CODE_OK" : "STATUS_CODE_ERROR",
      ...(input.error !== undefined ? { message: input.error } : {}),
    },
  };

  const childSpans = input.spans.map(
    (span, index): MlflowSpan => ({
      trace_id: traceIdBase64,
      span_id: spanIdFor(input.submissionId, index + 1),
      parent_span_id: rootSpanId,
      name: span.name,
      start_time_unix_nano: toUnixNano(Date.parse(span.startedAt)),
      end_time_unix_nano: toUnixNano(Date.parse(span.endedAt)),
      attributes: toOtlpAttributes(span.attributes),
      status: { code: "STATUS_CODE_OK" },
    }),
  );

  return {
    trace: {
      trace_info: {
        trace_id: `tr-${traceBytes.toString("hex")}`,
        trace_location: {
          type: "MLFLOW_EXPERIMENT",
          mlflow_experiment: { experiment_id: input.experimentId },
        },
        request_time: new Date(input.startedAtMs).toISOString(),
        execution_duration: `${durationSeconds.toFixed(3)}s`,
        state: input.state,
        trace_metadata: {
          "fabric.submission_id": input.submissionId,
          "fabric.agent": input.agent,
          "fabric.instance_id": input.instanceId,
          "fabric.session": input.session,
          ...(input.attemptCount !== undefined
            ? { "fabric.attempt_count": String(input.attemptCount) }
            : {}),
        },
        tags: {
          "fabric.agent": input.agent,
          ...(input.tenantId !== undefined ? { "fabric.tenant_id": input.tenantId } : {}),
        },
      },
      trace_data: { spans: [rootSpan, ...childSpans] },
    },
  };
}

function sha256(seed: string): Buffer {
  return createHash("sha256").update(seed).digest();
}

/** Deterministic 8-byte OTLP span id: sha-256 of `<submissionId>:span:<index>`. Index 0 is the root. */
function spanIdFor(submissionId: string, index: number): string {
  return sha256(`${submissionId}:span:${index}`).subarray(0, 8).toString("base64");
}

function toUnixNano(epochMs: number): string {
  return `${Math.round(epochMs)}000000`;
}

function toOtlpAttributes(attributes: Record<string, string | number | boolean>): OtlpAttribute[] {
  return Object.entries(attributes).map(([key, value]) => {
    if (typeof value === "number") {
      // OTLP proto3 JSON mapping: int64 serializes as a string.
      return Number.isInteger(value)
        ? { key, value: { int_value: String(value) } }
        : { key, value: { double_value: value } };
    }
    return { key, value: { string_value: String(value) } };
  });
}
